import type { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { BackendAvailabilityService } from '@core/pages/error-pages/backend-unavailable/services/backend-availability.service';
import { EnvironmentService } from '@core/services/environment.service';
import type { Observable } from 'rxjs';
import { catchError, throwError } from 'rxjs';

/*
 * Intercepts responses from the AML Desk backend and, on a network-level failure
 * (status 0), marks the backend as unavailable so the app can surface the
 * backend-unavailable page. The error is always rethrown so callers still see it.
 */
export const backendUnavailableInterceptor: HttpInterceptorFn = (request, next) => {
  const backendAvailabilityService = inject(BackendAvailabilityService);
  const environmentService = inject(EnvironmentService);

  /**
   * Determines whether the error should be treated as caused by backend unavailability,
   * based on the request details and error response.
   *
   * @param {HttpErrorResponse} error - The error response associated with the HTTP request.
   * @returns {boolean} - Returns `true` if the error is due to a network issue and the request targets the AML desk backend; otherwise, `false`.
   */
  const shouldHandleAsBackendUnavailable = (error: HttpErrorResponse): boolean => {
    if (backendAvailabilityService.isBackendUnavailable()) {
      return false;
    }

    const isNetworkError = error.status === 0;
    const isTargetsAmlDeskBackend = request.url.includes(environmentService.baseAdpHost);

    return isNetworkError && isTargetsAmlDeskBackend;
  };

  return next(request).pipe(
    catchError((error: HttpErrorResponse): Observable<never> => {
      if (shouldHandleAsBackendUnavailable(error)) {
        backendAvailabilityService.markUnavailable(error);
      }

      return throwError(() => error);
    }),
  );
};
